import { API_BASE } from "../config/api-url";
import { clearSession } from "../session/session";

export { API_BASE };

const DEFAULT_TIMEOUT_MS = 15000;

type ErrorPayload = { message?: string | string[]; error?: string } | null;

type ApiErrorDto = {
	code?: string;
	message?: string;
	details?: Array<{ field?: string; message?: string; code?: string }>;
	timestamp?: string;
	path?: string;
	requestId?: string;
};

type ApiEnvelope<T = unknown> = {
	success: boolean;
	data: T | null;
	error: ApiErrorDto | null;
	meta?: unknown;
};

type ResourceObject = {
	id: number | string;
	type: string;
	attributes: unknown;
};

const TYPE_OVERRIDES: Record<string, { singular?: string; plural?: string }> = {
	"service-provider": { singular: "service", plural: "services" },
	"dashboard-stats": { singular: "stats" },
};

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";
type RequestOptions = { auth?: boolean };

function isRecord(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object";
}

function isApiEnvelope(value: unknown): value is ApiEnvelope {
	return (
		isRecord(value) &&
		typeof value.success === "boolean" &&
		"data" in value &&
		"error" in value
	);
}

function isResourceObject(value: unknown): value is ResourceObject {
	const record = isRecord(value) ? value : null;
	if (!record) return false;
	return "id" in record && typeof record?.type === "string" && "attributes" in record;
}

function pluralize(type: string): string {
	const t = type.trim();
	if (!t) return "items";
	if (t.endsWith("ch") || t.endsWith("sh")) return `${t}es`;
	if (/[sxz]$/.test(t)) return `${t}es`;
	if (t.endsWith("y")) return `${t.slice(0, -1)}ies`;
	if (t.endsWith("s")) return `${t}es`;
	return `${t}s`;
}

function normalizeBackendData(value: unknown): unknown {
	// Single resource: { id, type, attributes }
	if (isResourceObject(value)) {
		const type = String(value.type || "").trim();
		const attrsRaw = value.attributes;
		const attrs =
			isRecord(attrsRaw) && !("id" in attrsRaw)
				? { id: value.id, ...(attrsRaw as Record<string, unknown>) }
				: attrsRaw;
		const override = type ? TYPE_OVERRIDES[type] : undefined;
		// Login endpoint returns type=session with attributes { user, token } and
		// the frontend historically expects { user, token } at top-level.
		if (type === "session") return attrs;
		if (override?.singular) return { [override.singular]: attrs };
		return type ? { [type]: attrs } : attrs;
	}

	// Collection of resources: [{ id, type, attributes }, ...]
	if (Array.isArray(value) && value.every((item) => isResourceObject(item))) {
		if (value.length === 0) return value;
		const type = String((value[0] as ResourceObject).type || "").trim();
		const override = type ? TYPE_OVERRIDES[type] : undefined;
		const key = type ? (override?.plural ?? pluralize(type)) : "items";
		return {
			[key]: value.map((item) => {
				const res = item as ResourceObject;
				const attrsRaw = res.attributes;
				if (isRecord(attrsRaw) && !("id" in attrsRaw)) {
					return { id: res.id, ...(attrsRaw as Record<string, unknown>) };
				}
				return attrsRaw;
			}),
		};
	}

	// Paginated resource collection: { items: ResourceObject[], meta: { pagination: ... } }
	if (isRecord(value) && Array.isArray(value.items)) {
		const rawItems = value.items as unknown[];
		if (rawItems.every((item) => isResourceObject(item))) {
			const first = rawItems[0] as ResourceObject | undefined;
			const type = String(first?.type || "").trim();
			const override = type ? TYPE_OVERRIDES[type] : undefined;
			const key = type ? (override?.plural ?? pluralize(type)) : "items";

			const metaRaw = isRecord(value.meta)
				? (value.meta as Record<string, unknown>)
				: undefined;
			const meta: Record<string, unknown> = metaRaw ? { ...metaRaw } : {};
			const pagination = isRecord(meta.pagination)
				? (meta.pagination as Record<string, unknown>)
				: undefined;
			const totalPages =
				typeof pagination?.totalPages === "number"
					? pagination.totalPages
					: undefined;
			const page =
				typeof pagination?.page === "number" ? pagination.page : undefined;
			const pageSize =
				typeof pagination?.pageSize === "number"
					? pagination.pageSize
					: undefined;
			const totalItems =
				typeof pagination?.totalItems === "number"
					? pagination.totalItems
					: undefined;

			// Compatibility with older frontend code paths.
			if (typeof totalPages === "number") meta.last_page = totalPages;
			if (typeof page === "number") meta.page = page;
			if (typeof pageSize === "number") meta.pageSize = pageSize;
			if (typeof totalItems === "number") meta.total_items = totalItems;

			return {
				[key]: rawItems.map((item) => {
					const res = item as ResourceObject;
					const attrsRaw = res.attributes;
					if (isRecord(attrsRaw) && !("id" in attrsRaw)) {
						return { id: res.id, ...(attrsRaw as Record<string, unknown>) };
					}
					return attrsRaw;
				}),
				meta,
			};
		}
	}

	return value;
}

function normalizeErrorMessage(payload: ErrorPayload, fallback: string): string {
	if (!payload) return fallback;
	if (typeof payload.error === "string" && payload.error.trim()) return payload.error;
	if (typeof payload.message === "string" && payload.message.trim())
		return payload.message;
	if (Array.isArray(payload.message) && payload.message.length) {
		return payload.message.filter(Boolean).join("\n");
	}
	return fallback;
}

function normalizeErrorMessageFromEnvelope(
	payload: ApiEnvelope | null | undefined,
	fallback: string,
): string {
	if (!payload) return fallback;
	const message =
		payload.error && typeof payload.error === "object"
			? (payload.error as ApiErrorDto).message
			: undefined;
	if (typeof message === "string" && message.trim()) return message;
	return fallback;
}

async function parseJsonSafe(res: Response): Promise<unknown> {
	try {
		return await res.json();
	} catch {
		return null;
	}
}

function forceLogoutToLogin(): void {
	clearSession();
	if (window.location.pathname !== "/login") {
		window.location.replace("/login");
	}
}

export async function requestApi<T>(
	method: HttpMethod,
	path: string,
	body?: unknown,
	options?: RequestOptions,
): Promise<T> {
	const controller = new AbortController();
	const timeout = window.setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

	const shouldAuth = options?.auth !== false;
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};

	const res = await fetch(`${API_BASE}${path}`, {
		method,
		headers,
		cache: "no-store",
		...(body === undefined ? {} : { body: JSON.stringify(body) }),
		signal: controller.signal,
		credentials: "include",
	});
	window.clearTimeout(timeout);

	const payload = await parseJsonSafe(res);

	// Errors (HTTP status)
	if (!res.ok) {
		if (shouldAuth && res.status === 401) {
			forceLogoutToLogin();
			throw new Error("Sesión expirada o sin permisos");
		}

		if (isApiEnvelope(payload)) {
			throw new Error(
				normalizeErrorMessageFromEnvelope(payload, `HTTP ${res.status}`),
			);
		}
		throw new Error(
			normalizeErrorMessage(payload as ErrorPayload, `HTTP ${res.status}`),
		);
	}

	// Success HTTP status but backend can still answer success=false
	if (isApiEnvelope(payload)) {
		if (!payload.success) {
			throw new Error(normalizeErrorMessageFromEnvelope(payload, "Error"));
		}
		return normalizeBackendData(payload.data) as T;
	}

	return normalizeBackendData(payload) as T;
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
	return requestApi<T>("POST", path, body);
}

export async function patchJson<T>(
	path: string,
	body: unknown,
	options?: RequestOptions,
): Promise<T> {
	return requestApi<T>("PATCH", path, body, options);
}

export async function deleteJson<T>(path: string, options?: RequestOptions): Promise<T> {
	return requestApi<T>("DELETE", path, undefined, options);
}

export async function postJsonWithOptions<T>(
	path: string,
	body: unknown,
	options?: RequestOptions,
): Promise<T> {
	return requestApi<T>("POST", path, body, options);
}

export async function getJson<T>(path: string): Promise<T> {
	return requestApi<T>("GET", path);
}

export async function getJsonWithOptions<T>(
	path: string,
	options?: RequestOptions,
): Promise<T> {
	return requestApi<T>("GET", path, undefined, options);
}

export async function postFormData<T>(
	path: string,
	formData: FormData,
): Promise<T> {
	const controller = new AbortController();
	const timeout = window.setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

	const headers: Record<string, string> = {};

	const res = await fetch(`${API_BASE}${path}`, {
		method: "POST",
		headers,
		cache: "no-store",
		body: formData,
		signal: controller.signal,
		credentials: "include",
	});
	window.clearTimeout(timeout);

	const payload = await parseJsonSafe(res);
	if (!res.ok) {
		if (isApiEnvelope(payload)) {
			throw new Error(
				normalizeErrorMessageFromEnvelope(payload, `HTTP ${res.status}`),
			);
		}
		throw new Error(
			normalizeErrorMessage(payload as ErrorPayload, `HTTP ${res.status}`),
		);
	}

	if (isApiEnvelope(payload)) {
		if (!payload.success) {
			throw new Error(normalizeErrorMessageFromEnvelope(payload, "Error"));
		}
		return normalizeBackendData(payload.data) as T;
	}

	return normalizeBackendData(payload) as T;
}

export async function patchFormData<T>(
	path: string,
	formData: FormData,
): Promise<T> {
	const controller = new AbortController();
	const timeout = window.setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

	const headers: Record<string, string> = {};

	const res = await fetch(`${API_BASE}${path}`, {
		method: "PATCH",
		headers,
		cache: "no-store",
		body: formData,
		signal: controller.signal,
		credentials: "include",
	});
	window.clearTimeout(timeout);

	const payload = await parseJsonSafe(res);
	if (!res.ok) {
		if (isApiEnvelope(payload)) {
			throw new Error(
				normalizeErrorMessageFromEnvelope(payload, `HTTP ${res.status}`),
			);
		}
		throw new Error(
			normalizeErrorMessage(payload as ErrorPayload, `HTTP ${res.status}`),
		);
	}

	if (isApiEnvelope(payload)) {
		if (!payload.success) {
			throw new Error(normalizeErrorMessageFromEnvelope(payload, "Error"));
		}
		return normalizeBackendData(payload.data) as T;
	}

	return normalizeBackendData(payload) as T;
}
